import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LoadingIndicator from "../components/LoadingIndicator";

import ln from "../../../assets/lines/line1.png";
import {
  searchDiscourse,
  getDiscourseTypes,
} from "../../../services/database/database_service";
import { useLanguage } from "../../../services/localization/LanguageContex";

function DiscourseScreenMain() {
  const { t, language } = useLanguage();
  const [discourseType, setDiscourseTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDiscourseType = async () => {
    try {
      const data = await getDiscourseTypes();
      const documents = data.documents;
      let tmpCategory = [];
      for (const doc of documents) {
        tmpCategory.push({
          enTitle: doc.discourseTypeName,
          dzTitle: doc.discourseTypeNameDz ?? "",
        });
      }
      setDiscourseTypes(tmpCategory);
    } catch (err) {
      setError(err.message); // Show a user-friendly message
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscourseType();
    window.scrollTo(0, 0);
  }, []);

  // Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const doSearch = async () => {
        if (searchTerm.trim() === "") {
          setSearchResults([]);
          return;
        }
        setIsSearching(true);
        try {
          const res = await searchDiscourse(searchTerm);
          setSearchResults(res.documents);
          // console.log("result", res.documents);
        } catch (e) {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      doSearch();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  return (
    <Box
      sx={{
        px: { xs: 3, sm: 6, md: 6 },
        pt: 2,
        pb: 6,
      }}
    >
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
        {t("Explore our Discourse")}
      </Typography>

      {/* 🔍 Search Bar */}
      <Box sx={{ maxWidth: 400, mx: "auto", mb: 4, position: "relative" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("Search discourse...")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* 🔍 Search results dropdown */}
        {searchTerm && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              bgcolor: "background.paper",
              boxShadow: 3,
              zIndex: 10,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: 1,
              mt: 1,
            }}
          >
            {isSearching ? (
              <Typography sx={{ p: 2 }} align="center">
                {t("Searching...")}...
              </Typography>
            ) : searchResults.length === 0 ? (
              <Typography sx={{ p: 2 }} align="center">
                {t("No results found")}
              </Typography>
            ) : (
              searchResults.map((result) => (
                <Link
                  key={result.$id}
                  to={`./result?search_query=${encodeURIComponent(
                    result.discourseTitle
                  )}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Box
                    sx={{
                      p: 2,
                      "&:hover": { bgcolor: "grey.100", cursor: "pointer" },
                    }}
                  >
                    <Typography variant="subtitle2">
                      {result.discourseTitle}
                    </Typography>
                  </Box>
                </Link>
              ))
            )}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <LoadingIndicator />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : discourseType.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No discourse found.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {discourseType.map((type) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={type["enTitle"]}>
              {/* Wrap the card with a Link */}
              <Link
                to={`./${encodeURIComponent(type["enTitle"])}`}
                style={{ textDecoration: "none" }}
              >
                <Card
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 180,
                    boxShadow: 5,
                    "&:hover": {
                      cursor: "pointer",
                      boxShadow: 7,
                      transform: "translateY(-2px)",
                    },
                    pl: 5,
                    pr: 5,
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      component="div"
                      align="center"
                      color="textPrimary"
                    >
                      {/* dz title from db */}
                      {language === "en"
                        ? type["enTitle"]
                        : type["dzTitle"] != null && type["dzTitle"] !== ""
                        ? type["dzTitle"]
                        : t(type["enTitle"])}
                    </Typography>
                    <Box
                      component="img"
                      src={ln}
                      alt="line"
                      loading="lazy"
                      sx={{
                        ml: 2,
                        mr: 2,
                        width: "100%", // Make the image take full width of the parent
                        height: "auto", // Maintain aspect ratio
                        maxWidth: "100%", // Prevent image from being stretched larger than its natural size
                        maxHeight: { xs: 50, sm: 100, md: 150 }, // Set different max height for responsive behavior (small, medium, and large screens)
                      }}
                    />
                  </CardContent>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default DiscourseScreenMain;

// import { Link } from "react-router-dom";
// import { useEffect,useState} from 'react';
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Box,
//   TextField,
//   InputAdornment,
// } from "@mui/material";
// import SearchIcon from "@mui/icons-material/Search";

// import ln from "../../../assets/lines/line1.png";
// import { searchDiscourse,getDiscourseTypes} from "../../../services/database/database_service";
// import { useLanguage } from "../../../services/localization/LanguageContex";

// function DiscourseScreenMain() {
//   const { t } = useLanguage();
//     const discourseType =['Phrases','Sentences','Stories'];
//       const [searchTerm, setSearchTerm] = useState("");
//       const [searchResults, setSearchResults] = useState([]);
//       const [isSearching, setIsSearching] = useState(false);

//       useEffect(() => {
//         window.scrollTo(0, 0);
//       }, []);

//         // Debounced search effect
//       useEffect(() => {
//           const delayDebounce = setTimeout(() => {
//             const doSearch = async () => {
//               if (searchTerm.trim() === "") {
//                 setSearchResults([]);
//                 return;
//               }
//               setIsSearching(true);
//               try {
//                 const res = await searchDiscourse(searchTerm);
//                 setSearchResults(res.documents);
//                 // console.log("result", res.documents);
//               } catch (e) {
//                 setSearchResults([]);
//               } finally {
//                 setIsSearching(false);
//               }
//             };
//             doSearch();
//           }, 300);

//           return () => clearTimeout(delayDebounce);
//         }, [searchTerm]);

// // const pathMap = {
// //   Signs: "./signstypeegory",
// //   Stories: "./stories",
// //   Quiz: "./quiz",
// //   Feedback: "./feedback",
// // };

//   return (
//     <Box sx={{ p: 6, pt: 2 }}>
//       <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
//        {t("Explore our discourse")}
//       </Typography>

//        {/* 🔍 Search Bar */}
//        <Box sx={{ maxWidth: 400, mx: "auto", mb: 4, position: "relative" }}>
//         <TextField
//           fullWidth
//           variant="outlined"
//           placeholder={t("Search discourse...")}
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <SearchIcon />
//               </InputAdornment>
//             ),
//           }}
//         />

//         {/* 🔍 Search results dropdown */}
//         {searchTerm && (
//           <Box
//             sx={{
//               position: "absolute",
//               top: "100%",
//               left: 0,
//               right: 0,
//               bgcolor: "background.paper",
//               boxShadow: 3,
//               zIndex: 10,
//               maxHeight: 300,
//               overflowY: "auto",
//               borderRadius: 1,
//               mt: 1,
//             }}
//           >
//             {isSearching ? (
//               <Typography sx={{ p: 2 }} align="center">
//                 {t("Searching...")}...
//               </Typography>
//             ) : searchResults.length === 0 ? (
//               <Typography sx={{ p: 2 }} align="center">
//                 {t("No results found")}
//               </Typography>
//             ) : (
//               searchResults.map((result) => (
//                 <Link
//                   key={result.$id}
//                   to={`./result?search_query=${encodeURIComponent(
//                     result.discourseTitle
//                   )}`}
//                   style={{ textDecoration: "none", color: "inherit" }}
//                 >
//                   <Box
//                     sx={{
//                       p: 2,
//                       "&:hover": { bgcolor: "grey.100", cursor: "pointer" },
//                     }}
//                   >
//                     <Typography variant="subtitle2">
//                       {result.discourseTitle}
//                     </Typography>

//                   </Box>
//                 </Link>
//               ))
//             )}
//           </Box>
//         )}
//       </Box>

//       <Grid container spacing={4}>
//         {discourseType.map((type) => (
//           <Grid item xs={12} sm={6} md={4}
//           lg={3}
//           key={type}>
//             {/* Wrap the card with a Link */}
//             <Link
//               to={`./${encodeURIComponent(type)}`}
//               style={{ textDecoration: "none" }}
//             >
//               <Card
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   alignItems: "center",
//                   minHeight: 180,
//                   boxShadow: 5,
//                   "&:hover": {
//                     cursor: "pointer",
//                     boxShadow: 7,
//                     transform: "translateY(-2px)",
//                   },
//                   pl: 5,
//                   pr: 5,
//                 }}
//               >
//                 <CardContent
//                   sx={{
//                     display: "flex",
//                     flexDirection: "column",
//                     alignItems: "center",
//                   }}
//                 >
//                   <Typography
//                     variant="subtitle1"
//                     component="div"
//                     align="center"
//                     color="textPrimary"
//                   >
//                     {t(type)}
//                   </Typography>
//                   <Box
//                     component="img"
//                     src={ln}
//                     alt="line"
//                     loading="lazy"
//                     sx={{
//                       ml: 2,
//                       mr: 2,
//                       width: "100%", // Make the image take full width of the parent
//                       height: "auto", // Maintain aspect ratio
//                       maxWidth: "100%", // Prevent image from being stretched larger than its natural size
//                       maxHeight: { xs: 50, sm: 100, md: 150 }, // Set different max height for responsive behavior (small, medium, and large screens)
//                     }}
//                   />

//                   {/* <Box
//                       component="img"
//                       src={ln}
//                       alt="line"
//                       loading="lazy"
//                       sx={{ ml: 2, mr: 2 }}
//                     /> */}
//                 </CardContent>
//               </Card>
//             </Link>
//           </Grid>
//         ))}
//       </Grid>

//       {/* {loading && stories? (
//         <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
//           <CircularProgress />
//         </Box>
//       ) : (
//         <Grid container spacing={4}>
//           {stories.map((story) => (
//             <Grid item xs={12} sm={6} md={4} lg={3} key={story.$id}>
//               <Link
//                 to={`./${encodeURIComponent(story.$id)}`}
//                 style={{ textDecoration: "none" }}
//               >
//                 <Card
//                   sx={{
//                     bgcolor: "transparent",
//                     boxShadow: "none",
//                   }}
//                 >
//                   <CardContent sx={{ p: 0 }}>
//                     <YouTubeThumbnail
//                       videoUrl={story.storyUrl}
//                       altText={story.storyTitle}
//                     />
//                     <Typography
//                       variant="subtitle1"
//                       noWrap
//                       sx={{ mt: 1, px: 1 }}
//                     >
//                       {story.storyTitle}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               </Link>
//             </Grid>
//           ))}
//         </Grid>
//       )} */}
//     </Box>
//   );
// }

// export default DiscourseScreenMain;
